import { useState } from 'react';
import { addToast } from '@heroui/react';
import {
  useLazyGetTurnitinEulaStatusQuery,
  useLazyGetTurnitinEulaInfoQuery,
  useLazyGetTurnitinEulaPageQuery,
  useAcceptTurnitinEulaMutation,
  TurnitinEulaLanguage,
} from '@/entities/Turnitin';
import type { IAssignmentSubmissionRequest } from '@/entities/Assignment';

type EulaCheckResult = {
  canProceed: boolean;
  needsAcceptance: boolean;
  isOutdated: boolean;
  // Язык возвращаем значением, а не читаем из состояния: setCurrentLanguage применится
  // только к следующему рендеру, а страницу соглашения запрашивают сразу после проверки.
  language: TurnitinEulaLanguage;
};

export const useTurnitinEulaCheck = () => {
  const [isEulaModalOpen, setIsEulaModalOpen] = useState(false);
  const [isEulaLoading, setIsEulaLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(false); // Флаг проверки EULA
  const [currentLanguage, setCurrentLanguage] = useState<TurnitinEulaLanguage>('en-US');
  const [eulaHtmlContent, setEulaHtmlContent] = useState<string>('');
  const [pendingSubmission, setPendingSubmission] = useState<IAssignmentSubmissionRequest | null>(
    null,
  );
  const [pendingCallback, setPendingCallback] = useState<
    ((data: IAssignmentSubmissionRequest) => Promise<void>) | null
  >(null);

  const [fetchEulaStatus] = useLazyGetTurnitinEulaStatusQuery();
  const [fetchEulaInfo] = useLazyGetTurnitinEulaInfoQuery();
  const [fetchEulaPage] = useLazyGetTurnitinEulaPageQuery();
  const [acceptEula, { isLoading: isAccepting }] = useAcceptTurnitinEulaMutation();

  // Проверка статуса и версии EULA
  const checkEulaStatus = async (): Promise<EulaCheckResult> => {
    try {
      // Шаг 1: Получить статус EULA пользователя
      const statusResp = await fetchEulaStatus().unwrap();
      // Шаг 2: Если не принят - нужно показать модалку
      if (!statusResp.accepted || !statusResp.version) {
        setCurrentLanguage('en-US'); // По умолчанию английский
        return {
          canProceed: false,
          needsAcceptance: true,
          isOutdated: false,
          language: 'en-US',
        };
      }

      // Шаг 3: Если принят - проверить версию
      const infoResp = await fetchEulaInfo({ lang: statusResp.language }).unwrap();
      // Сравнить версии
      const isOutdated = statusResp.version !== infoResp.version;

      if (isOutdated) {
        setCurrentLanguage(statusResp.language);
        return {
          canProceed: false,
          needsAcceptance: false,
          isOutdated: true,
          language: statusResp.language,
        };
      }

      // Всё ОК
      return {
        canProceed: true,
        needsAcceptance: false,
        isOutdated: false,
        language: statusResp.language,
      };
    } catch (error) {
      addToast({
        title: 'Unable to check EULA status. Please try again.',
        color: 'danger',
      });
      throw error;
    }
  };

  // Открыть модалку EULA
  const openEulaModal = async (
    isOutdated: boolean = false,
    lang: TurnitinEulaLanguage = currentLanguage,
  ) => {
    setIsEulaModalOpen(true);
    setIsEulaLoading(true);

    try {
      const result = await fetchEulaPage({ lang });

      // Извлекаем HTML контент из результата
      let htmlContent: string | null = null;

      // Сначала проверяем data
      if (result.data && typeof result.data === 'string') {
        htmlContent = result.data;
      }
      // Если data нет, но есть error с HTML в message - извлекаем оттуда
      else if (result.error) {
        const errorMessage = (result.error as any)?.message || '';
        // Проверяем, не является ли "ошибка" на самом деле HTML контентом
        if (
          errorMessage.trim().startsWith('<') &&
          (errorMessage.includes('</html>') || errorMessage.includes('</body>'))
        ) {
          console.log('[openEulaModal] HTML found in error.message, extracting');
          htmlContent = errorMessage;
        } else {
          console.error('[openEulaModal] Real error in result:', result.error);
          throw result.error;
        }
      }

      if (htmlContent) {
        setEulaHtmlContent(htmlContent);
      } else {
        console.error('[openEulaModal] No HTML content found in result');
        throw new Error('No HTML content received');
      }
    } catch (error: any) {
      // В catch также проверяем, не является ли ошибка HTML контентом
      const errorMessage = error?.message || '';
      if (
        errorMessage.trim().startsWith('<') &&
        (errorMessage.includes('</html>') || errorMessage.includes('</body>'))
      ) {
        setEulaHtmlContent(errorMessage);
      } else {
        console.error('[openEulaModal] Error details:', {
          message: error?.message,
          data: error?.data,
          status: error?.status,
        });
        addToast({
          title: 'Unable to load EULA page. Please try again.',
          color: 'danger',
        });
        setIsEulaModalOpen(false);
        setIsEulaLoading(false);
        return;
      }
    }

    const description = isOutdated
      ? 'A new version is available. Please review and accept it to continue.'
      : 'Please accept to submit your submission.';

    addToast({
      title: 'Turnitin EULA',
      description,
      color: 'default',
    });
  };

  // Закрыть модалку
  const closeEulaModal = () => {
    setIsEulaModalOpen(false);
    setIsEulaLoading(false);
    setEulaHtmlContent('');
    setPendingSubmission(null);
    setPendingCallback(null);
  };

  // Обработка загрузки iframe
  const handleEulaLoaded = () => {
    // Iframe загружен, можно убрать лоадер если был
    setIsEulaLoading(false);
  };

  // Принятие EULA и выполнение отложенного действия
  const handleEulaAccept = async () => {
    try {
      await acceptEula({ language: currentLanguage }).unwrap();

      addToast({
        title: 'EULA accepted successfully.',
        color: 'success',
      });

      closeEulaModal();

      // Если есть отложенное действие - выполнить его
      if (pendingCallback && pendingSubmission) {
        await pendingCallback(pendingSubmission);
      }

      return true;
    } catch (error) {
      addToast({
        title: 'Failed to accept EULA. Please try again.',
        color: 'danger',
      });
      return false;
    }
  };

  // Проверка перед отправкой submission с возможностью автоматической отправки после принятия EULA
  const checkBeforeSubmit = async (
    submissionData: IAssignmentSubmissionRequest,
    submitCallback: (data: IAssignmentSubmissionRequest) => Promise<void>,
  ): Promise<boolean> => {
    setIsChecking(true); // Начало проверки
    try {
      const result = await checkEulaStatus();
      if (result.canProceed) {
        return true;
      }

      // Сохранить данные для отправки после принятия EULA
      setPendingSubmission(submissionData);
      setPendingCallback(() => submitCallback);

      // Показать модалку для принятия/обновления
      await openEulaModal(result.isOutdated, result.language);
      return false;
    } catch (error) {
      console.error('[checkBeforeSubmit] Error during check:', error);
      return false;
    } finally {
      setIsChecking(false);
    }
  };

  return {
    // Состояние
    isEulaModalOpen,
    isEulaLoading,
    isAccepting,
    isChecking, // Флаг проверки EULA
    currentLanguage,
    eulaHtmlContent,

    // Методы
    checkBeforeSubmit,
    closeEulaModal,
    handleEulaLoaded,
    handleEulaAccept,
  };
};
